import { Formik, Form as FormikForm } from 'formik';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import {
  useRef,
  useCallback,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { NotifyPeopleTriggerButton } from 'hb-react/frontoffice/components/NotifyPeopleModal/NotifyPeopleTriggerButton';
import { transportationOptions } from 'hb-react/frontoffice/components/TripFormModal/constants/transportationsOption';
import { useInitialValues } from 'hb-react/frontoffice/components/TripFormModal/utils/useInitialValues';
import UserTargeting from 'hb-react/frontoffice/components/user_targeting';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Columns, { Column } from 'hb-react/shared/components/Columns';
import mentionsOptions from 'hb-react/shared/components/DesignSystem/QuillEditor/utils/mentionsOptions';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import {
  Location,
  Date,
  Select,
} from 'hb-react/shared/components/formik/components/inputs';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import QuillEditorWrappedV3 from 'hb-react/shared/components/QuillEditorWrappedV3';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';

const Form = forwardRef(
  (
    {
      canDisplayEmojiPicker,
      isLoading,
      isUpdatingTrip,
      modalElement,
      modalHeaderId,
      onCloseModal,
      onCreate,
      onUpdate,
      onToggleEmojiPicker,
      trip,
    },
    ref,
  ) => {
    const { flagEvUserPicturesToGlobalStorage } = useFeatureFlag();
    const [isSaving, setIsSaving] = useState(false);
    const [notifyPeoples, setNotifyPeoples] = useState([]);

    const { currentUser } = useDefaultAppContexts();
    const isMobileDevice = useIsMobileDevice();

    const formikRef = useRef();
    const userTargetingRef = useRef();

    const initialValues = useInitialValues({ trip });

    // User can't notify on edit
    const userCanNotify = useMemo(
      () =>
        isUpdatingTrip
          ? false
          : currentUser.notify_rights.user_can_notify_on_trip_location,
      [currentUser, isUpdatingTrip],
    );
    const quillEditorInputName = 'trip[content]';
    const tomorrowDate = dayjs().add(1, 'day').format('YYYY-MM-DD');

    // Share function(s) in ref.
    useImperativeHandle(ref, () => ({
      canDisplayDiscardChangesModal: () =>
        !isEqual(formikRef.current.initialValues, formikRef.current.values),
    }));

    // TODO: Remove when removing flagHivehackNotifyPeople
    const handleOpenNotifyModal = useCallback(() => {
      // Select current notifyPeoples in UserTargeting modal if the user re-opens it
      // Only working on desktop by current UserTargeting app design
      if (userTargetingRef.current && !isMobileDevice) {
        userTargetingRef.current.selectUsers(notifyPeoples);
      }
    }, [notifyPeoples, userTargetingRef.current]);

    const handleValidateNotifySelection = useCallback(
      (selectedUsers, selectedUsersIds) => {
        if (!isMobileDevice) {
          setNotifyPeoples(selectedUsers);
        }
        if (formikRef.current) {
          formikRef.current.setFieldValue(
            'targeted_user_ids',
            selectedUsersIds,
          );
        }
      },
      [formikRef.current],
    );

    const handleSubmit = useCallback(
      async (values) => {
        setIsSaving(true);

        try {
          let promise;

          if (isUpdatingTrip) {
            promise = axios.put(
              Routes.user_trip_path(currentUser.id, trip.id),
              values,
            );
          } else {
            promise = axios.post(
              Routes.user_trips_path(currentUser.id),
              values,
            );
          }

          await promise
            .then(({ data }) => {
              if (isUpdatingTrip) {
                displayNotif('success', __('Successfully updated'));
                if (onUpdate) {
                  onUpdate(data.trip);
                }
              } else {
                displayNotif('success', __('Successfully created'));
                if (onCreate) {
                  onCreate(data.trip);
                }
              }

              onCloseModal();
            })
            .catch((error) => {
              const endDateError = error?.response?.data?.errors?.end_date;

              setIsSaving(false);
              if (endDateError) {
                formikRef.current.setFieldError('trip[end_date]', endDateError);
              }
            });
        } catch (_error) {
          setIsSaving(false);
        }
      },
      [currentUser, trip, isUpdatingTrip, onUpdate, onCreate],
    );

    if (isLoading) {
      return null;
    }

    return (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          formikRef.current = formik;

          return (
            <FormikForm style={{ width: '100%' }}>
              <RequiredExplanation mt={0} mb={5} />

              <Box
                display={{ _: 'block', m: 'flex' }}
                mb={{ _: 6, m: 8 }}
                alignItems={{ _: 'inherit', m: 'flex-start' }}
              >
                <Box alignItems="center" mb={{ _: 6, m: 0 }}>
                  <AvatarWrapper>
                    <Image
                      src={
                        flagEvUserPicturesToGlobalStorage
                          ? currentUser.new_photo?.thumbnail_url
                          : currentUser.photo_thumb_url
                      }
                      alt=""
                    />
                  </AvatarWrapper>
                  <Box
                    flex={1}
                    font="h3-semibold"
                    display={{ _: 'flex', m: 'none' }}
                  >
                    {currentUser.name}
                  </Box>
                </Box>

                <Box
                  flex={1}
                  flexDirection="column"
                  width={{ _: '100%', m: '1%' }}
                >
                  <QuillEditorWrappedV3
                    autoFocus
                    ariaLabelledBy={modalHeaderId}
                    containerElement={modalElement}
                    minHeight={100}
                    name={quillEditorInputName}
                    onToggleEmojiPicker={onToggleEmojiPicker}
                    options={{
                      embedLy: false,
                      toolbar: {
                        images: false,
                        attachments: false,
                        links: true,
                        emojiPicker: canDisplayEmojiPicker,
                        mentions: mentionsOptions,
                      },
                      toolbarFloats: [
                        {
                          key: 'link',
                          items: [
                            {
                              type: 'link',
                            },
                          ],
                        },
                      ],
                    }}
                    placeholder={__('Add a message about your trip')}
                  />
                </Box>
              </Box>

              <Columns>
                <Column>
                  <Location
                    fieldAddressName="location"
                    name="trip[departure_location_attributes]"
                    label={I18n.t('web.me.trips.form.departure')}
                  />
                </Column>
                <Column>
                  <Location
                    name="trip"
                    label={I18n.t('web.me.trips.form.destination')}
                    validate={{
                      required: true,
                    }}
                  />
                </Column>
              </Columns>

              <Columns>
                <Column>
                  <Date
                    name="trip[start_date]"
                    label={I18n.t('web.me.trips.form.begin_date')}
                    validate={{
                      required: true,
                    }}
                    maxDate={formik?.values?.trip?.end_date}
                  />
                </Column>
                <Column>
                  <Date
                    name="trip[end_date]"
                    label={I18n.t('web.me.trips.form.end_date')}
                    validate={{
                      required: true,
                    }}
                    // End date must be equal or after the start date (or after today if the start date is not set or in the past).
                    minDate={
                      formik.values?.trip?.start_date
                        ? dayjs(formik.values?.trip?.start_date).isAfter(
                            tomorrowDate,
                            'day',
                          )
                          ? formik.values?.trip?.start_date
                          : tomorrowDate
                        : tomorrowDate
                    }
                  />
                </Column>
              </Columns>

              <Columns>
                <Column>
                  <Select
                    type="timezone"
                    name="trip[timezone]"
                    isClearable
                    label={I18n.t('views.backoffice.timezone')}
                    validate={{
                      required: true,
                    }}
                  />
                </Column>
                <Column>
                  <Select
                    options={transportationOptions}
                    name="trip[transportation]"
                    isClearable
                    label={I18n.t('web.me.trips.form.transportation.name')}
                    validate={{
                      required: true,
                    }}
                  />
                </Column>
              </Columns>

              <Box justifyContent="flex-end" mt={7}>
                {userCanNotify && (
                  <UserTargeting
                    defaultSelectedUsers={isMobileDevice ? [] : notifyPeoples}
                    ref={userTargetingRef}
                    renderTriggerElement={(onClick) => (
                      <NotifyPeopleTriggerButton
                        values={formik.values}
                        isDisabled={isSaving}
                        onClick={onClick}
                      />
                    )}
                    openByClickOn={
                      <Button
                        aria-hidden="true"
                        as="span"
                        dataTestId="user-targeting-button"
                        // Don't put double loader, just put primary button on loading state
                        isDisabled={isSaving}
                        icon={() => (
                          <FontAwesomeIcon
                            iconName="bell"
                            height="16px"
                            iconType={CATEGORIES.SOLID}
                          />
                        )}
                        variant="secondary"
                      >
                        {__('Notify people')}
                        {formik.values?.targeted_user_ids?.length > 0 &&
                          ` · ${formik.values?.targeted_user_ids.length}`}
                      </Button>
                    }
                    defaultQuery={{
                      targeting_type: trip?.type,
                    }}
                    maxTargetedUsers={trip?.user_targeting?.max_targeted_users}
                    onOpen={handleOpenNotifyModal}
                    onValidateSelection={handleValidateNotifySelection}
                  />
                )}
                <Button type="submit" isLoading={isSaving} ml={3}>
                  {isUpdatingTrip ? __('Save changes') : __('Publish')}
                </Button>
              </Box>

              <ErrorFocus />
            </FormikForm>
          );
        }}
      </Formik>
    );
  },
);

Form.propTypes = {
  canDisplayEmojiPicker: PropTypes.bool,
  isLoading: PropTypes.bool,
  isUpdatingTrip: PropTypes.bool,
  modalElement: PropTypes.object,
  modalHeaderId: PropTypes.string,
  onCloseModal: PropTypes.func,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  trip: PropTypes.object,
};

export default Form;

const AvatarWrapper = styled(Box).attrs(() => ({
  height: { _: 48, m: 60 },
  minWidth: { _: 48, m: 60 },
  mr: 3,
  width: { _: 48, m: 60 },
}))`
  user-select: none;

  img {
    border-radius: 50%;
    width: 100%;
  }
`;
